import { trackAction } from '@mint-lib/datadog';
import { useTranslation } from '@mint-lib/i18n';
import { WarningAltFilled } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  ConfirmationDialog,
  ConfirmationDialogAction,
  ConfirmationDialogCancel,
  ConfirmationDialogContent,
  ConfirmationDialogDescription,
  ConfirmationDialogFooter,
  ConfirmationDialogHeader,
  ConfirmationDialogOverlay,
  ConfirmationDialogPortal,
  ConfirmationDialogRoot,
  ConfirmationDialogTitle,
} from '@mint-lib/tw-components';

import type { UserTableV2Row } from '../datasource.js';
import type useUserRowActions from '../hooks/useUserRowActions.jsx';

type DeleteUserMutation = ReturnType<typeof useUserRowActions>['deleteUser'];

type Props = {
  target: UserTableV2Row;
  deleteUser: DeleteUserMutation;
  onClose: () => void;
  onDeleted: () => void;
};

const DeleteUserConfirmationDialog = ({
  target,
  deleteUser,
  onClose,
  onDeleted,
}: Props) => {
  const { t } = useTranslation('@myn/permissions');

  const handleConfirmDelete = () => {
    onClose();
    deleteUser.mutate(
      { urlParams: { id: target.id } },
      {
        onSuccess: () => {
          trackAction('User management: remove user');
          enqueueSnackbar({
            removable: true,
            variant: 'success',
            title: t('Success'),
            textLayout: 'multiline',
            subTitle: t('Users removed from workspace.'),
          });
          onDeleted();
        },
        onError: () => {
          enqueueSnackbar({
            removable: true,
            variant: 'error',
            title: t('Failure'),
            textLayout: 'multiline',
            subTitle: t('Something went wrong. Please try again'),
          });
        },
      },
    );
  };

  return (
    <ConfirmationDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <ConfirmationDialogPortal>
        <ConfirmationDialogOverlay />
        <ConfirmationDialogRoot>
          <ConfirmationDialogHeader onClose={onClose}>
            <WarningAltFilled className="size-4 fill-warning-main" />
            <ConfirmationDialogTitle>
              {t('Remove user from workspace?')}
            </ConfirmationDialogTitle>
          </ConfirmationDialogHeader>
          <ConfirmationDialogContent>
            <ConfirmationDialogDescription>
              {t(
                'This action will remove the user from this workspace only. Their account will remain active and they may still access other workspaces.',
              )}
            </ConfirmationDialogDescription>
          </ConfirmationDialogContent>
          <ConfirmationDialogFooter>
            <ConfirmationDialogCancel onClick={onClose}>
              {t('Cancel')}
            </ConfirmationDialogCancel>
            <ConfirmationDialogAction
              color="warning"
              onClick={handleConfirmDelete}
            >
              {t('Yes, remove')}
            </ConfirmationDialogAction>
          </ConfirmationDialogFooter>
        </ConfirmationDialogRoot>
      </ConfirmationDialogPortal>
    </ConfirmationDialog>
  );
};

export default DeleteUserConfirmationDialog;
